// src/api/blogAPI.js
import { mockPosts, mockCategories, mockTags, mockUsers } from "./mockData";

// 模拟异步延迟
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// 从localStorage获取用户文章
const getUserPosts = () => {
  const userPosts = localStorage.getItem("userPosts");
  return userPosts ? JSON.parse(userPosts) : [];
};

// 保存用户文章到localStorage
const saveUserPosts = (posts) => {
  localStorage.setItem("userPosts", JSON.stringify(posts));
};

const storedUsersKey = "registeredUsers";

const getStoredUsers = () => {
  const storedUsers = localStorage.getItem(storedUsersKey);
  if (!storedUsers) {
    return [];
  }

  try {
    const parsed = JSON.parse(storedUsers);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const saveStoredUsers = (users) => {
  localStorage.setItem(storedUsersKey, JSON.stringify(users));
};

const getAllUsers = () => [...mockUsers, ...getStoredUsers()];

const userMatchesCredentials = (user, credentials) => {
  if (credentials.email && user.email === credentials.email) return true;
  if (credentials.phone && user.phone === credentials.phone) return true;
  if (credentials.username && user.username === credentials.username)
    return true;
  return false;
};

const passwordMatches = (user, password) => {
  if (user.password) {
    return user.password === password;
  }
  return password === "password";
};

class BlogAPI {
  // 获取文章列表
  async getPosts(page = 1, pageSize = 10, filters = {}) {
    await delay(500);

    // 合并模拟文章和用户创建的文章
    let allPosts = [...mockPosts, ...getUserPosts()];

    let filteredPosts = [...allPosts];

    // 应用过滤条件
    if (filters.categoryId) {
      filteredPosts = filteredPosts.filter(
        (post) => post.categoryId === filters.categoryId
      );
    }

    if (filters.tag) {
      filteredPosts = filteredPosts.filter((post) =>
        post.tags.includes(filters.tag)
      );
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredPosts = filteredPosts.filter(
        (post) =>
          post.title.toLowerCase().includes(searchLower) ||
          post.content.toLowerCase().includes(searchLower) ||
          post.tags.some((tag) => tag.toLowerCase().includes(searchLower))
      );
    }

    // 分页处理
    const total = filteredPosts.length;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const data = filteredPosts.slice(start, end);

    return {
      data,
      pagination: {
        current: page,
        pageSize,
        total,
        totalPages: Math.ceil(total / pageSize),
      },
    };
  }

  // 获取文章详情
  async getPostById(id) {
    await delay(300);
    // 合并模拟文章和用户创建的文章
    const allPosts = [...mockPosts, ...getUserPosts()];
    const post = allPosts.find((p) => p.id === parseInt(id));
    if (!post) {
      throw new Error("文章未找到");
    }
    return post;
  }

  // 创建文章
  async createPost(postData) {
    await delay(800);

    // 获取现有用户文章
    const userPosts = getUserPosts();

    const newPost = {
      id:
        Math.max(
          ...mockPosts.map((p) => p.id),
          ...userPosts.map((p) => p.id),
          0
        ) + 1,
      ...postData,
      publishedDate: new Date().toISOString(),
      views: 0,
      comments: [],
    };

    // 添加到用户文章并保存
    userPosts.push(newPost);
    saveUserPosts(userPosts);

    return newPost;
  }

  // 更新文章
  async updatePost(id, postData) {
    await delay(600);

    // 获取用户文章
    let userPosts = getUserPosts();
    const userPostIndex = userPosts.findIndex((p) => p.id === parseInt(id));

    if (userPostIndex !== -1) {
      // 更新用户文章
      userPosts[userPostIndex] = { ...userPosts[userPostIndex], ...postData };
      saveUserPosts(userPosts);
      return userPosts[userPostIndex];
    }

    // 如果在用户文章中找不到，则在模拟文章中查找
    const mockPostIndex = mockPosts.findIndex((p) => p.id === parseInt(id));
    if (mockPostIndex === -1) {
      throw new Error("文章未找到");
    }

    // 注意：出于演示目的，这里允许更新模拟文章
    // 在真实应用中，你可能只想让用户更新自己的文章
    mockPosts[mockPostIndex] = { ...mockPosts[mockPostIndex], ...postData };
    return mockPosts[mockPostIndex];
  }

  // 删除文章
  async deletePost(id) {
    await delay(400);

    // 获取用户文章
    let userPosts = getUserPosts();
    const userPostIndex = userPosts.findIndex((p) => p.id === parseInt(id));

    if (userPostIndex !== -1) {
      // 从用户文章中删除
      userPosts.splice(userPostIndex, 1);
      saveUserPosts(userPosts);
      return { success: true };
    }

    // 如果在用户文章中找不到，则在模拟文章中查找
    const mockPostIndex = mockPosts.findIndex((p) => p.id === parseInt(id));
    if (mockPostIndex === -1) {
      throw new Error("文章未找到");
    }

    // 注意：出于演示目的，这里允许删除模拟文章
    // 在真实应用中，你可能只想让用户删除自己的文章
    mockPosts.splice(mockPostIndex, 1);
    return { success: true };
  }

  // 添加评论
  async addComment(postId, commentData) {
    await delay(500);

    // 查找所有文章（包括用户创建的）
    const allPosts = [...mockPosts, ...getUserPosts()];
    const post = allPosts.find((p) => p.id === parseInt(postId));
    if (!post) {
      throw new Error("文章未找到");
    }

    const newComment = {
      id: Math.max(...post.comments.map((c) => c.id), 0) + 1,
      ...commentData,
      createdAt: new Date().toISOString(),
    };

    post.comments.push(newComment);

    // 如果是用户文章，需要更新存储
    const userPosts = getUserPosts();
    const userPostIndex = userPosts.findIndex((p) => p.id === parseInt(postId));
    if (userPostIndex !== -1) {
      userPosts[userPostIndex] = post;
      saveUserPosts(userPosts);
    }

    return newComment;
  }

  // 获取分类列表
  async getCategories() {
    await delay(300);
    return mockCategories;
  }

  // 获取标签列表
  async getTags() {
    await delay(300);
    return mockTags;
  }

  // 获取热门文章
  async getPopularPosts(limit = 5) {
    await delay(300);
    // 合并所有文章并排序
    const allPosts = [...mockPosts, ...getUserPosts()];
    return [...allPosts].sort((a, b) => b.views - a.views).slice(0, limit);
  }

  // 用户登录
  async login(credentials) {
    await delay(600);
    const allUsers = getAllUsers();
    const user = allUsers.find((u) => userMatchesCredentials(u, credentials));

    if (!user || !passwordMatches(user, credentials.password)) {
      throw new Error("账号或密码错误");
    }

    return {
      user,
      token: `mock-jwt-token-${user.id}`,
    };
  }

  async register(userData) {
    await delay(800);
    const allUsers = getAllUsers();
    const existingUser = allUsers.find(
      (u) =>
        (userData.email && u.email === userData.email) ||
        (userData.username && u.username === userData.username) ||
        (userData.phone && u.phone === userData.phone)
    );
    if (existingUser) {
      throw new Error("账号已存在");
    }

    const maxId = allUsers.reduce(
      (max, user) => Math.max(max, user.id || 0),
      0
    );
    const newUser = {
      id: maxId + 1,
      ...userData,
      role: "user",
    };

    const storedUsers = getStoredUsers();
    storedUsers.push(newUser);
    saveStoredUsers(storedUsers);
    return {
      user: newUser,
      token: `mock-jwt-token-${newUser.id}`,
    };
  }

  async getCurrentUser(token) {
    await delay(300);
    const userId = parseInt(token.split("-")[3]);
    const user = getAllUsers().find((u) => u.id === userId);
    if (!user) {
      throw new Error("用户未找到");
    }
    return user;
  }
}

// 创建API实例
const api = new BlogAPI();

export default api;
